// Firebase functions for startup opportunity operations
import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  addDoc,
  updateDoc, 
  deleteDoc,
  query, 
  where, 
  orderBy, 
  limit,
  startAfter,
  serverTimestamp
} from "firebase/firestore";
import { db } from "./firebase-config";

// Add a simple test to verify db connection
if (!db) {
  console.error('❌ Firestore database not initialized properly');
} else {
  console.log('✅ Firestore database initialized successfully');
}

/**
 * Helper function to fetch company data for opportunities
 * @param {Array} opportunities - Array of opportunities
 * @returns {Promise<Array>} - Opportunities with company data attached
 */
const enrichOpportunitiesWithCompanyData = async (opportunities) => {
  if (!opportunities || opportunities.length === 0) {
    return opportunities;
  }

  // Get unique company IDs
  const companyIds = [...new Set(
    opportunities
      .map(opp => opp.companyId)
      .filter(id => id && id.trim() !== '')
  )];

  if (companyIds.length === 0) {
    return opportunities;
  }

  // Fetch company data
  const companyDataMap = {};
  
  try {
    // Fetch companies in batches (Firestore 'in' query limit is 10)
    const batchSize = 10;
    for (let i = 0; i < companyIds.length; i += batchSize) {
      const batch = companyIds.slice(i, i + batchSize);
      
      const companiesQuery = query(
        collection(db, 'companies'),
        where('__name__', 'in', batch)
      );
      
      const companiesSnapshot = await getDocs(companiesQuery);
      
      companiesSnapshot.docs.forEach(doc => {
        companyDataMap[doc.id] = {
          id: doc.id,
          ...doc.data()
        };
      });
    }
  } catch (error) {
    console.warn('Failed to fetch company data:', error);
    // Continue without company data if fetch fails
  }

  // Attach company data to opportunities
  return opportunities.map(opportunity => ({
    ...opportunity,
    company: opportunity.companyId ? companyDataMap[opportunity.companyId] || null : null
  }));
};

/**
 * Data validation functions
 */

/**
 * Validates startup opportunity data
 * @param {Object} opportunityData - The opportunity data to validate
 * @returns {Object} - Validation result with success status and errors
 */
export const validateOpportunityData = (opportunityData) => {
  const errors = [];
  
  // Required fields
  if (!opportunityData.companyName || opportunityData.companyName.trim() === '') {
    errors.push('Company name is required');
  }
  
  if (!opportunityData.companyDescription || opportunityData.companyDescription.trim() === '') {
    errors.push('Company description is required');
  }
  
  if (!opportunityData.roleArea || opportunityData.roleArea.trim() === '') {
    errors.push('Role area is required');
  }
  
  if (!opportunityData.roleDescription || opportunityData.roleDescription.trim() === '') {
    errors.push('Role description is required');
  }
  
  if (!opportunityData.fundingStage || opportunityData.fundingStage.trim() === '') {
    errors.push('Funding stage is required');
  }
  
  if (!opportunityData.experienceLevel || opportunityData.experienceLevel.trim() === '') {
    errors.push('Experience level is required');
  }
  
  // Validate founder info if provided
  if (opportunityData.founderInfo) {
    if (!opportunityData.founderInfo.name || opportunityData.founderInfo.name.trim() === '') {
      errors.push('Founder name is required when founder info is provided');
    }
    if (!opportunityData.founderInfo.title || opportunityData.founderInfo.title.trim() === '') {
      errors.push('Founder title is required when founder info is provided');
    }
  }
  
  // Validate arrays
  if (opportunityData.leadInvestors && !Array.isArray(opportunityData.leadInvestors)) {
    errors.push('Lead investors must be an array');
  }
  
  if (opportunityData.highlights && !Array.isArray(opportunityData.highlights)) {
    errors.push('Highlights must be an array');
  }
  
  // Validate status
  const validStatuses = ['active', 'paused', 'filled'];
  if (opportunityData.status && !validStatuses.includes(opportunityData.status)) {
    errors.push('Status must be one of: active, paused, filled');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Validates connection request data
 * @param {Object} requestData - The connection request data to validate
 * @returns {Object} - Validation result with success status and errors
 */
export const validateConnectionRequestData = (requestData) => {
  const errors = [];
  
  // Required fields
  if (!requestData.userId || requestData.userId.trim() === '') {
    errors.push('User ID is required');
  }
  
  if (!requestData.opportunityId || requestData.opportunityId.trim() === '') {
    errors.push('Opportunity ID is required');
  }
  
  if (!requestData.userMessage || requestData.userMessage.trim() === '') {
    errors.push('User message is required');
  }
  
  // Validate message length
  if (requestData.userMessage && requestData.userMessage.length > 1000) {
    errors.push('User message must be 1000 characters or less');
  }
  
  // Validate status
  const validStatuses = ['pending', 'approved', 'introduced', 'declined'];
  if (requestData.status && !validStatuses.includes(requestData.status)) {
    errors.push('Status must be one of: pending, approved, introduced, declined');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * CRUD operations for startup opportunities
 */

/**
 * Create a new startup opportunity
 * @param {Object} opportunityData - The opportunity data
 * @returns {Promise<Object>} - Success status and created opportunity data or error
 */
export const createStartupOpportunity = async (opportunityData) => {
  try {
    console.log('Creating startup opportunity:', opportunityData);
    
    // Validate data
    const validation = validateOpportunityData(opportunityData);
    if (!validation.isValid) {
      return { 
        success: false, 
        error: 'Validation failed', 
        validationErrors: validation.errors 
      };
    }
    
    // Prepare opportunity data with defaults
    const opportunityToCreate = {
      ...opportunityData,
      isFlexible: opportunityData.isFlexible !== undefined ? opportunityData.isFlexible : true,
      status: opportunityData.status || 'active',
      leadInvestors: opportunityData.leadInvestors || [],
      highlights: opportunityData.highlights || [],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    // Add to Firestore
    const docRef = await addDoc(collection(db, 'startup_opportunities'), opportunityToCreate);
    
    return {
      success: true,
      data: {
        id: docRef.id,
        ...opportunityToCreate
      }
    };
  } catch (error) {
    console.error('Error creating startup opportunity:', error);
    return {
      success: false,
      error: error.message || 'Failed to create startup opportunity'
    };
  }
};

/**
 * Get startup opportunities with pagination and filtering
 * @param {Object} options - Query options
 * @returns {Promise<Object>} - Success status and opportunities data or error
 */
export const getStartupOpportunities = async (options = {}) => {
  try {
    console.log('🔥 getStartupOpportunities called with options:', options);
    
    const {
      limit: limitCount = 20,
      lastVisible = null,
      // Support for multiple filter values
      companyStages = [],
      roleAreas = [],
      experienceLevels = [],
      // Legacy single filter support (for backward compatibility)
      fundingStage = null,
      roleArea = null,
      experienceLevel = null,
      status = 'active',
      searchTerm = null,
      orderByField = 'createdAt',
      orderDirection = 'desc'
    } = options;
    
    let opportunitiesQuery = collection(db, 'startup_opportunities');
    
    // Build query constraints
    const queryConstraints = [];
    
    // Add status filter
    if (status) {
      queryConstraints.push(where('status', '==', status));
    }
    
    // Handle company stages filtering
    const stagesToFilter = companyStages.length > 0 ? companyStages : (fundingStage ? [fundingStage] : []);
    if (stagesToFilter.length > 0) {
      if (stagesToFilter.length === 1) {
        queryConstraints.push(where('fundingStage', '==', stagesToFilter[0]));
      } else {
        queryConstraints.push(where('fundingStage', 'in', stagesToFilter));
      }
    }
    
    // Handle role areas filtering
    const rolesToFilter = roleAreas.length > 0 ? roleAreas : (roleArea ? [roleArea] : []);
    if (rolesToFilter.length > 0) {
      if (rolesToFilter.length === 1) {
        queryConstraints.push(where('roleArea', '==', rolesToFilter[0]));
      } else {
        queryConstraints.push(where('roleArea', 'in', rolesToFilter));
      }
    }
    
    // Handle experience levels filtering
    const experienceToFilter = experienceLevels.length > 0 ? experienceLevels : (experienceLevel ? [experienceLevel] : []);
    if (experienceToFilter.length > 0) {
      if (experienceToFilter.length === 1) {
        queryConstraints.push(where('experienceLevel', '==', experienceToFilter[0]));
      } else {
        queryConstraints.push(where('experienceLevel', 'in', experienceToFilter));
      }
    }
    
    // Add ordering
    queryConstraints.push(orderBy(orderByField, orderDirection));
    
    // Add pagination
    if (lastVisible) {
      queryConstraints.push(startAfter(lastVisible));
    }
    
    // Add limit (increase for client-side filtering)
    const queryLimit = searchTerm ? Math.max(limitCount * 2, 50) : limitCount;
    queryConstraints.push(limit(queryLimit));
    
    // Apply constraints
    opportunitiesQuery = query(opportunitiesQuery, ...queryConstraints);
    
    // Execute query
    console.log('🔍 Executing Firestore query...');
    const snapshot = await getDocs(opportunitiesQuery);
    console.log('📋 Query result - docs found:', snapshot.docs.length);
    
    if (snapshot.empty) {
      console.log('📭 No opportunities found in database');
      return {
        success: true,
        data: {
          opportunities: [],
          lastVisible: null,
          hasMore: false
        }
      };
    }
    
    // Transform data
    let opportunities = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Enrich with company data
    opportunities = await enrichOpportunitiesWithCompanyData(opportunities);
    
    // Apply client-side search filtering if search term is provided
    if (searchTerm && searchTerm.trim()) {
      opportunities = applySearchFilter(opportunities, searchTerm.trim());
    }
    
    // Get last document for pagination (before potential trimming)
    const lastDoc = snapshot.docs[snapshot.docs.length - 1];
    
    // Trim results to requested limit if we fetched more for search
    if (searchTerm && opportunities.length > limitCount) {
      opportunities = opportunities.slice(0, limitCount);
    }

    return {
      success: true,
      data: {
        opportunities,
        lastVisible: lastDoc,
        hasMore: snapshot.docs.length === queryLimit && (!searchTerm || opportunities.length === limitCount)
      }
    };
  } catch (error) {
    console.error('Error fetching startup opportunities:', error);
    return {
      success: false,
      error: error.message || 'Failed to fetch startup opportunities'
    };
  }
};

/**
 * Apply search filtering to opportunities
 * @param {Array} opportunities - Array of opportunities to filter
 * @param {string} searchTerm - Search term to filter by
 * @returns {Array} - Filtered opportunities
 */
const applySearchFilter = (opportunities, searchTerm) => {
  if (!searchTerm || !searchTerm.trim()) {
    return opportunities;
  }
  
  const term = searchTerm.toLowerCase();
  const searchWords = term.split(/\s+/).filter(word => word.length > 0);
  
  return opportunities.filter(opp => {
    // Create searchable text from opportunity data
    const searchableText = [
      opp.companyName,
      opp.companyDescription,
      opp.roleArea,
      opp.roleTitle,
      opp.roleDescription,
      opp.fundingStage,
      opp.experienceLevel,
      opp.location,
      ...(opp.leadInvestors || []),
      ...(opp.highlights || []),
      opp.founderInfo?.name,
      opp.founderInfo?.title,
      opp.founderInfo?.bio,
      // Include company data in search
      opp.company?.name,
      opp.company?.description,
      ...(opp.company?.industries || [])
    ].filter(Boolean).join(' ').toLowerCase();
    
    // Check if all search words are found in the searchable text
    return searchWords.every(word => searchableText.includes(word));
  });
};

/**
 * Get a specific startup opportunity by ID
 * @param {string} opportunityId - The ID of the opportunity
 * @returns {Promise<Object>} - Success status and opportunity data or error
 */
export const getStartupOpportunityById = async (opportunityId) => {
  try {
    console.log('Fetching startup opportunity:', opportunityId);
    
    if (!opportunityId) {
      return { success: false, error: 'Opportunity ID is required' };
    }
    
    const docRef = doc(db, 'startup_opportunities', opportunityId);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      return { success: false, error: 'Startup opportunity not found' };
    }
    
    const opportunity = {
      id: docSnap.id,
      ...docSnap.data()
    };
    
    // Enrich with company data
    const enrichedOpportunities = await enrichOpportunitiesWithCompanyData([opportunity]);
    
    return {
      success: true,
      data: enrichedOpportunities[0]
    };
  } catch (error) {
    console.error('Error fetching startup opportunity:', error);
    return {
      success: false,
      error: error.message || 'Failed to fetch startup opportunity'
    };
  }
};

/**
 * Update a startup opportunity
 * @param {string} opportunityId - The ID of the opportunity to update
 * @param {Object} updateData - The data to update
 * @param {boolean} skipValidation - Skip full validation for simple updates like status changes
 * @returns {Promise<Object>} - Success status and updated data or error
 */
export const updateStartupOpportunity = async (opportunityId, updateData, skipValidation = false) => {
  try {
    console.log('Updating startup opportunity:', opportunityId, updateData);
    
    if (!opportunityId) {
      return { success: false, error: 'Opportunity ID is required' };
    }
    
    // Validate update data unless skipping validation for simple updates
    if (!skipValidation) {
      const validation = validateOpportunityData(updateData);
      if (!validation.isValid) {
        return { 
          success: false, 
          error: 'Validation failed', 
          validationErrors: validation.errors 
        };
      }
    }
    
    const docRef = doc(db, 'startup_opportunities', opportunityId);
    
    // Check if opportunity exists
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) {
      return { success: false, error: 'Startup opportunity not found' };
    }
    
    // Update with timestamp
    const dataToUpdate = {
      ...updateData,
      updatedAt: serverTimestamp()
    };
    
    await updateDoc(docRef, dataToUpdate);
    
    return {
      success: true,
      data: {
        id: opportunityId,
        ...dataToUpdate
      }
    };
  } catch (error) {
    console.error('Error updating startup opportunity:', error);
    return {
      success: false,
      error: error.message || 'Failed to update startup opportunity'
    };
  }
};

/**
 * Update opportunity status only (lightweight update)
 * @param {string} opportunityId - The ID of the opportunity to update
 * @param {string} status - The new status ('active', 'paused', 'filled')
 * @returns {Promise<Object>} - Success status and updated data or error
 */
export const updateOpportunityStatus = async (opportunityId, status) => {
  try {
    console.log('Updating opportunity status:', opportunityId, status);
    
    if (!opportunityId) {
      return { success: false, error: 'Opportunity ID is required' };
    }
    
    // Validate status
    const validStatuses = ['active', 'paused', 'filled'];
    if (!validStatuses.includes(status)) {
      return { success: false, error: 'Status must be one of: active, paused, filled' };
    }
    
    const docRef = doc(db, 'startup_opportunities', opportunityId);
    
    // Check if opportunity exists
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) {
      return { success: false, error: 'Startup opportunity not found' };
    }
    
    // Update status with timestamp
    const dataToUpdate = {
      status,
      updatedAt: serverTimestamp()
    };
    
    await updateDoc(docRef, dataToUpdate);
    
    return {
      success: true,
      data: {
        id: opportunityId,
        status,
        updatedAt: new Date()
      }
    };
  } catch (error) {
    console.error('Error updating opportunity status:', error);
    return {
      success: false,
      error: error.message || 'Failed to update opportunity status'
    };
  }
};

/**
 * Delete a startup opportunity
 * @param {string} opportunityId - The ID of the opportunity to delete
 * @returns {Promise<Object>} - Success status or error
 */
export const deleteStartupOpportunity = async (opportunityId) => {
  try {
    console.log('Deleting startup opportunity:', opportunityId);
    
    if (!opportunityId) {
      return { success: false, error: 'Opportunity ID is required' };
    }
    
    const docRef = doc(db, 'startup_opportunities', opportunityId);
    
    // Check if opportunity exists
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) {
      return { success: false, error: 'Startup opportunity not found' };
    }
    
    await deleteDoc(docRef);
    
    return {
      success: true,
      data: { id: opportunityId, deletedAt: new Date().toISOString() }
    };
  } catch (error) {
    console.error('Error deleting startup opportunity:', error);
    return {
      success: false,
      error: error.message || 'Failed to delete startup opportunity'
    };
  }
};

/**
 * CRUD operations for connection requests
 */

/**
 * Create a connection request
 * @param {Object} requestData - The connection request data
 * @returns {Promise<Object>} - Success status and created request data or error
 */
export const createConnectionRequest = async (requestData) => {
  try {
    console.log('Creating connection request:', requestData);
    
    // Validate data
    const validation = validateConnectionRequestData(requestData);
    if (!validation.isValid) {
      return { 
        success: false, 
        error: 'Validation failed', 
        validationErrors: validation.errors 
      };
    }
    
    // Check if user already has a pending request for this opportunity
    const existingRequestQuery = query(
      collection(db, 'connection_requests'),
      where('userId', '==', requestData.userId),
      where('opportunityId', '==', requestData.opportunityId),
      where('status', 'in', ['pending', 'approved', 'introduced'])
    );
    
    const existingRequestSnapshot = await getDocs(existingRequestQuery);
    
    if (!existingRequestSnapshot.empty) {
      const existingRequest = existingRequestSnapshot.docs[0].data();
      const statusMessage = existingRequest.status === 'introduced' 
        ? 'You have already been introduced for this opportunity'
        : 'You already have a pending connection request for this opportunity';
      
      return {
        success: false,
        error: statusMessage
      };
    }
    
    // Prepare request data with defaults
    const requestToCreate = {
      ...requestData,
      status: requestData.status || 'pending',
      adminNotes: requestData.adminNotes || '',
      createdAt: serverTimestamp(),
      processedAt: null,
      processedBy: null
    };
    
    // Add to Firestore
    const docRef = await addDoc(collection(db, 'connection_requests'), requestToCreate);
    
    return {
      success: true,
      data: {
        id: docRef.id,
        ...requestToCreate
      }
    };
  } catch (error) {
    console.error('Error creating connection request:', error);
    return {
      success: false,
      error: error.message || 'Failed to create connection request'
    };
  }
};

/**
 * Get connection requests with filtering options
 * @param {Object} options - Query options
 * @returns {Promise<Object>} - Success status and requests data or error
 */
export const getConnectionRequests = async (options = {}) => {
  try {
    const {
      limit: limitCount = 50,
      lastVisible = null,
      status = null,
      userId = null,
      opportunityId = null,
      orderByField = 'createdAt',
      orderDirection = 'desc'
    } = options;
    
    let requestsQuery = collection(db, 'connection_requests');
    
    // Build query constraints
    const queryConstraints = [];
    
    // Add filters
    if (status) {
      queryConstraints.push(where('status', '==', status));
    }
    
    if (userId) {
      queryConstraints.push(where('userId', '==', userId));
    }
    
    if (opportunityId) {
      queryConstraints.push(where('opportunityId', '==', opportunityId));
    }
    
    // Add ordering
    queryConstraints.push(orderBy(orderByField, orderDirection));
    
    // Add pagination
    if (lastVisible) {
      queryConstraints.push(startAfter(lastVisible));
    }
    
    // Add limit
    queryConstraints.push(limit(limitCount));
    
    // Apply constraints
    requestsQuery = query(requestsQuery, ...queryConstraints);
    
    // Execute query
    const snapshot = await getDocs(requestsQuery);
    
    if (snapshot.empty) {
      return {
        success: true,
        data: {
          requests: [],
          lastVisible: null,
          hasMore: false
        }
      };
    }
    
    // Get last document for pagination
    const lastDoc = snapshot.docs[snapshot.docs.length - 1];
    
    // Transform data
    const requests = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    return {
      success: true,
      data: {
        requests,
        lastVisible: lastDoc,
        hasMore: requests.length === limitCount
      }
    };
  } catch (error) {
    console.error('Error fetching connection requests:', error);
    return {
      success: false,
      error: error.message || 'Failed to fetch connection requests'
    };
  }
};

/**
 * Update a connection request status
 * @param {string} requestId - The ID of the request to update
 * @param {Object} updateData - The data to update
 * @returns {Promise<Object>} - Success status and updated data or error
 */
export const updateConnectionRequest = async (requestId, updateData) => {
  try {
    console.log('Updating connection request:', requestId, updateData);
    
    if (!requestId) {
      return { success: false, error: 'Request ID is required' };
    }
    
    const docRef = doc(db, 'connection_requests', requestId);
    
    // Check if request exists
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) {
      return { success: false, error: 'Connection request not found' };
    }
    
    // Prepare update data
    const dataToUpdate = {
      ...updateData,
      updatedAt: serverTimestamp()
    };
    
    // If status is being updated to processed states, add processed timestamp
    if (updateData.status && ['approved', 'introduced', 'declined'].includes(updateData.status)) {
      dataToUpdate.processedAt = serverTimestamp();
      if (updateData.processedBy) {
        dataToUpdate.processedBy = updateData.processedBy;
      }
    }
    
    await updateDoc(docRef, dataToUpdate);
    
    return {
      success: true,
      data: {
        id: requestId,
        ...dataToUpdate
      }
    };
  } catch (error) {
    console.error('Error updating connection request:', error);
    return {
      success: false,
      error: error.message || 'Failed to update connection request'
    };
  }
};

/**
 * Get connection requests for a specific user
 * @param {string} userId - The ID of the user
 * @returns {Promise<Object>} - Success status and user's requests data or error
 */
export const getUserConnectionRequests = async (userId) => {
  try {
    console.log('Fetching connection requests for user:', userId);
    
    if (!userId) {
      return { success: false, error: 'User ID is required' };
    }
    
    return await getConnectionRequests({ userId });
  } catch (error) {
    console.error('Error fetching user connection requests:', error);
    return {
      success: false,
      error: error.message || 'Failed to fetch user connection requests'
    };
  }
};

/**
 * Check if user has an existing connection request for an opportunity
 * @param {string} userId - The ID of the user
 * @param {string} opportunityId - The ID of the opportunity
 * @returns {Promise<Object>} - Success status and connection request status
 */
export const checkUserConnectionRequest = async (userId, opportunityId) => {
  try {
    if (!userId || !opportunityId) {
      return { 
        success: false, 
        error: 'User ID and Opportunity ID are required' 
      };
    }

    // Query for existing connection requests
    const existingRequestQuery = query(
      collection(db, 'connection_requests'),
      where('userId', '==', userId),
      where('opportunityId', '==', opportunityId),
      where('status', 'in', ['pending', 'approved', 'introduced'])
    );

    const existingRequestSnapshot = await getDocs(existingRequestQuery);

    if (existingRequestSnapshot.empty) {
      return {
        success: true,
        data: {
          hasRequest: false,
          status: null,
          request: null
        }
      };
    }

    const existingRequest = existingRequestSnapshot.docs[0];
    const requestData = existingRequest.data();

    return {
      success: true,
      data: {
        hasRequest: true,
        status: requestData.status,
        request: {
          id: existingRequest.id,
          ...requestData
        }
      }
    };
  } catch (error) {
    console.error('Error checking user connection request:', error);
    return {
      success: false,
      error: error.message || 'Failed to check connection request'
    };
  }
};

/**
 * Get pending connection requests for admin review
 * @returns {Promise<Object>} - Success status and pending requests data or error
 */
export const getPendingConnectionRequests = async () => {
  try {
    console.log('Fetching pending connection requests');
    
    return await getConnectionRequests({ 
      status: 'pending',
      orderByField: 'createdAt',
      orderDirection: 'asc' // Oldest first for admin queue
    });
  } catch (error) {
    console.error('Error fetching pending connection requests:', error);
    return {
      success: false,
      error: error.message || 'Failed to fetch pending connection requests'
    };
  }
};